/**
 * VideoExporter.ts
 * -----------------------------------------------------------------------------
 * What this file does
 * -----------------------------------------------------------------------------
 * This exporter produces short animated exports from the Three.js viewer in two formats:
 *
 *   1) GIF  - Uses gif.js and stores frames as HTMLCanvasElements in memory.
 *   2) MP4  - Uses ffmpeg.wasm and writes frames incrementally as JPEGs into the
 *            virtual filesystem (no huge in-memory PNG arrays).
 */

import * as THREE from 'three';
import Grid from '../Geodata/grid';
import { camera } from '../Global/global_variables';
import { renderer } from '../Global/global_variables';
import { grid } from '../Global/global_variables';
import { FFmpeg } from '@ffmpeg/ffmpeg';

const workerCode = await fetch('/visualizer/gif.worker.js').then((res) => res.text());
const blob = new Blob([workerCode], { type: 'application/javascript' });
const gifWorkerBlobURL = URL.createObjectURL(blob);

import { getSegments } from '../Gui/store/persistent/persistentStore.js';
import { getElement } from '../Gui/store/persistent/persistentStore.js';
import { RepaintProgress as CommitProgressUI } from '../Gui/components/panels/ProgressBar';
import { postProcess } from '../Global/global_variables';
import { switcher } from 'Global/Switcher';
import { getViewer } from 'store/viewerStore';

// -------------------------------
// Export settings
// -------------------------------

export type VideoExportFormat = 'GIF' | 'MP4';
export type VideoExportVariant = 'lookaround' | 'orbit' | 'timelapse' | 'screenrecording';

export type ResolutionPreset = 'match_viewer' | '1080p' | '1440p' | '2160p';

export const RESOLUTION_PRESETS: Record<ResolutionPreset, { maxW: number; maxH: number; label: string }> = {
    match_viewer: {
        maxW: Number.MAX_SAFE_INTEGER,
        maxH: Number.MAX_SAFE_INTEGER,
        label: 'Match Viewer',
    },
    '1080p': {
        maxW: 1920,
        maxH: 1080,
        label: '1080p',
    },
    '1440p': {
        maxW: 2560,
        maxH: 1440,
        label: '1440p',
    },
    '2160p': {
        maxW: 3840,
        maxH: 2160,
        label: '4K',
    },
};

export type VideoExportSettings = {
    durationSec: number; // e.g. 5, 10
    fps: number; // e.g. 24, 25, 30
    resolution: ResolutionPreset; // preset-based resolution selection
    dprCap: number; // usually 1 for stability
    jpegQuality: number; // intermediate frame quality (0..1)
    mp4Quality: number; // 0..100
    keepViewerSize: boolean; // do not visually shrink canvas
};

// Reasonable defaults (safe + good UX)
const DEFAULT_EXPORT_SETTINGS: VideoExportSettings = {
    durationSec: 5,
    fps: 25,
    resolution: '1080p',
    dprCap: 1,
    jpegQuality: 0.8,
    mp4Quality: 80,
    keepViewerSize: true,
};

export const FREE_SCREEN_RECORDING_MAX_DURATION_SEC = 120;

export type FreeScreenRecordingResult = {
    data: Uint8Array;
    extension: 'mp4' | 'gif';
};

export class VideoExporter {
    gif: any;
    mp4Writer: any;
    aviWriter: any;
    format: string = 'GIF';

    // Left only for compatibility with gifs
    private frameCanvases: HTMLCanvasElement[] = [];

    // MP4 path: write JPEG frames into ffmpeg FS
    private ffmpeg: any | null = null;
    private framesWritten = 0;

    // export settings
    private exportFps = 25;
    private exportMaxW = 2560;
    private exportMaxH = 1440;
    private exportDprCap = 1;

    // MP4 intermediate frame format
    private frameExt: 'jpg' = 'jpg';
    private jpegQuality = 0.92;

    // UI quality (0..100), used for GIF + MP4 encode settings
    quality: number = 80;
    easeIn: boolean = true;

    // Timelapse: cache last dataset frame applied
    private lastTimelapseFrameApplied: number | null = null;

    // MP4 write pipeline
    private pendingFrameWrites: Promise<void>[] = [];
    private maxPendingFrameWrites = 8;

    // Free recording
    private freeRec: {
        format: VideoExportFormat;
        fps: number;
        mode: 'mediarecorder' | 'framecapture';
        snap: {
            w: number;
            h: number;
            pixelRatio: number;
            canvasWidth: number;
            canvasHeight: number;
            styleWidth: string;
            styleHeight: string;
            clientWidth: number;
            clientHeight: number;
        };
        startedAtMs: number;
        stoppedAtMs: number | null;
        targetW: number;
        targetH: number;

        captureW: number;
        captureH: number;

        mediaRecorder: MediaRecorder | null;
        mediaStream: MediaStream | null;
        sourceStream: MediaStream | null;
        canvasCaptureTrack: (MediaStreamTrack & { requestFrame?: () => void }) | null;
        recordedChunks: Blob[];
        recordedMimeType: string | null;
        stopPromise: Promise<void> | null;

        running: boolean;
        rafId: number | null;
        lastFrameTs: number;
        frameIntervalMs: number;

        pendingWrites: Promise<void>[];
        maxPendingWrites: number;
    } | null = null;

    // Cancellation
    private cancelRequested = false;
    private cancelledError = new Error('VIDEO_EXPORT_CANCELLED');

    constructor(private readonly setProgress: (progress: number | undefined) => void) {}

    private updateProgress(progress: number | undefined) {
        this.setProgress(progress);
    }

    // -------------------------------
    // UI scheduling helpers
    // Export loops are expensive; if we update React/UI every frame,
    // the viewer stutters badly. We throttle UI commits + yield to the browser.
    // -------------------------------

    private uiUpdateEveryNFrames = 25; // progress + UI commit frequency
    private yieldEveryNFrames = 30; // how often we yield to let the browser paint

    private async flushUI() {
        // Keeps existing behavior but reads nicer at call sites.
        await CommitProgressUI();
    }

    private async maybeUpdateUI(i: number, frames: number) {
        // Update only every N frames (and always on the last frame)
        if (i % this.uiUpdateEveryNFrames !== 0 && i !== frames - 1) return;
        this.updateProgress(Math.round(((i + 1) / frames) * 50));
        await this.flushUI();
    }

    private async maybeYieldToBrowser(i: number) {
        // Let the browser paint / handle input occasionally
        if (i % this.yieldEveryNFrames !== 0) return;
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }

    // -------------------------------
    // FFmpeg initialization
    // -------------------------------
    private async initFFmpeg() {
        if (this.ffmpeg) return;

        this.ffmpeg = new FFmpeg();
        await this.ffmpeg.load();
        this.framesWritten = 0;
    }

    // -------------------------------
    // MP4 Pipeline
    // -------------------------------
    private async queueFrameWrite(r: THREE.WebGLRenderer) {
        this.throwIfCancelled();

        // start write without awaiting immediately
        // here we catch as well errors from the cancellation of the video
        const p = this.writeFrameFromRenderer(r).catch((e) => {
            if (this.cancelRequested) return;
            if (e?.message?.includes('FFmpeg.terminate')) return;
            throw e;
        });

        this.pendingFrameWrites.push(p);

        // backpressure: every N frames, wait for the batch
        if (this.pendingFrameWrites.length >= this.maxPendingFrameWrites) {
            await Promise.all(this.pendingFrameWrites);
            this.pendingFrameWrites.length = 0;
        }
    }

    private async flushFrameWrites() {
        if (this.pendingFrameWrites.length === 0) return;
        await Promise.all(this.pendingFrameWrites);
        this.pendingFrameWrites.length = 0;
    }

    private queueFreeRecordingFrame(r: THREE.WebGLRenderer) {
        const rec = this.freeRec;
        if (!rec || this.cancelRequested || !this.ffmpeg) return;

        if (rec.pendingWrites.length >= rec.maxPendingWrites) return;

        const p = this.writeFrameFromRenderer(r)
            .catch((e) => {
                if (this.cancelRequested) return;
                if (e?.message?.includes('FFmpeg.terminate')) return;
                throw e;
            })
            .finally(() => {
                const idx = rec.pendingWrites.indexOf(p);
                if (idx >= 0) rec.pendingWrites.splice(idx, 1);
            });

        rec.pendingWrites.push(p);
    }

    // -------------------------------
    // Renderer snapshot / restore
    // -------------------------------
    private getRendererSnapshot(r: THREE.WebGLRenderer) {
        const size = new THREE.Vector2();
        r.getSize(size);

        const pixelRatio = (r as any).getPixelRatio ? (r as any).getPixelRatio() : 1;
        const canvas = r.domElement as HTMLCanvasElement;

        return {
            w: size.x,
            h: size.y,
            pixelRatio,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            styleWidth: canvas.style.width || '',
            styleHeight: canvas.style.height || '',
            clientWidth: canvas.clientWidth,
            clientHeight: canvas.clientHeight,
        };
    }

    private restoreRenderer(
        r: THREE.WebGLRenderer,
        snap: {
            w: number;
            h: number;
            pixelRatio: number;
            canvasWidth: number;
            canvasHeight: number;
            styleWidth: string;
            styleHeight: string;
            clientWidth: number;
            clientHeight: number;
        }
    ) {
        const canvas = r.domElement as HTMLCanvasElement;

        (r as any).setPixelRatio?.(snap.pixelRatio);

        r.setSize(snap.w, snap.h, false);
        postProcess?.composer?.setSize?.(snap.w, snap.h);

        canvas.style.width = snap.styleWidth || '';
        canvas.style.height = snap.styleHeight || '';
    }

    // -------------------------------
    private canvasToArrayBuffer(canvas: HTMLCanvasElement, type: 'image/jpeg', quality: number): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            canvas.toBlob(
                async (blob) => {
                    try {
                        if (!blob) return reject(new Error('toBlob returned null'));
                        const buf = await blob.arrayBuffer();
                        resolve(buf);
                    } catch (e) {
                        reject(e);
                    }
                },
                type,
                quality
            );
        });
    }

    private blobToUint8Array(blob: Blob): Promise<Uint8Array> {
        return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
    }

    private renderCurrentViewerFrame(r: THREE.WebGLRenderer, scene: THREE.Scene) {
        if (postProcess?.composer) {
            postProcess.applyPostProcess?.(0);
            postProcess.composer.render();
            return;
        }

        r.render(scene, camera);
    }

    private async applyPreferredCaptureConstraints(track: MediaStreamTrack | null, width: number, height: number, fps: number) {
        if (!track?.applyConstraints) return;

        try {
            await track.applyConstraints({
                width: { ideal: Math.max(2, Math.floor(width)) },
                height: { ideal: Math.max(2, Math.floor(height)) },
                frameRate: { ideal: Math.max(1, fps) },
                ...({ resizeMode: 'crop-and-scale' } as any),
            } as MediaTrackConstraints);
        } catch (error) {
            console.warn('Unable to apply preferred screen-capture constraints.', error);
        }
    }

    private getSupportedRecordingMimeType(): string | null {
        if (typeof MediaRecorder === 'undefined') return null;
        if (typeof MediaRecorder.isTypeSupported !== 'function') return 'video/webm';

        // Avoid native browser MP4/H.264 canvas capture here. Some Chromium builds
        // produce green macroblock corruption from canvas MediaRecorder MP4 streams.
        const candidates = ['video/webm;codecs=vp8', 'video/webm;codecs=vp9', 'video/webm', 'video/mp4;codecs=avc1.42E01E', 'video/mp4'];
        for (const candidate of candidates) {
            if (MediaRecorder.isTypeSupported(candidate)) {
                return candidate;
            }
        }

        return null;
    }

    private getRecordingVideoBitrate(width: number, height: number, fps: number) {
        const pixelsPerFrame = Math.max(1, width * height);
        const qualityFactor = Math.max(0.35, Math.min(1, this.quality / 100));
        const bitsPerPixel = 0.1 + qualityFactor * 0.1;

        return Math.max(4_000_000, Math.min(80_000_000, Math.round(pixelsPerFrame * Math.max(1, fps) * bitsPerPixel)));
    }

    private waitForRecorderStop(stopPromise: Promise<void> | null, timeoutMs = 15000): Promise<boolean> {
        if (!stopPromise) return Promise.resolve(true);

        return new Promise<boolean>((resolve) => {
            const timer = window.setTimeout(() => {
                resolve(false);
            }, timeoutMs);

            stopPromise.then(
                () => {
                    window.clearTimeout(timer);
                    resolve(true);
                },
                () => {
                    window.clearTimeout(timer);
                    resolve(false);
                }
            );
        });
    }

    private async execFFmpegWithTimeout(args: string[], timeoutMs = 600000) {
        if (!this.ffmpeg) {
            await this.initFFmpeg();
        }

        let timeoutId: number | null = null;

        try {
            return await Promise.race([
                this.ffmpeg.exec(args),
                new Promise<never>((_, reject) => {
                    timeoutId = window.setTimeout(() => {
                        try {
                            this.ffmpeg?.terminate?.();
                        } catch {}
                        this.ffmpeg = null;
                        reject(new Error('FFmpeg timed out during MP4 conversion'));
                    }, timeoutMs);
                }),
            ]);
        } finally {
            if (timeoutId != null) {
                window.clearTimeout(timeoutId);
            }
        }
    }

    private async transcodeRecordedBlobToMp4(
        inputBlob: Blob,
        opts?: { sourceW?: number; sourceH?: number; targetW?: number; targetH?: number; durationSec?: number }
    ): Promise<Uint8Array> {
        this.throwIfCancelled();

        await this.initFFmpeg();

        const inputExt = inputBlob.type.includes('mp4') ? 'mp4' : 'webm';
        const inputName = `input.${inputExt}`;

        await this.ffmpeg.writeFile(inputName, await this.blobToUint8Array(inputBlob));

        let qv = Math.round((100 - this.quality) / 3);
        qv = Math.min(Math.max(qv, 1), 31);

        const needScale =
            opts?.targetW != null &&
            opts?.targetH != null &&
            opts?.sourceW != null &&
            opts?.sourceH != null &&
            (opts.targetW !== opts.sourceW || opts.targetH !== opts.sourceH);

        const vf = needScale
            ? `scale=${opts!.targetW}:${opts!.targetH}:flags=bilinear,pad=ceil(iw/2)*2:ceil(ih/2)*2`
            : 'pad=ceil(iw/2)*2:ceil(ih/2)*2';

        try {
            const timeoutMs = Math.max(600000, Math.ceil((opts?.durationSec ?? 0) * 8000));

            await this.execFFmpegWithTimeout(
                [
                    '-fflags',
                    '+genpts',
                    '-i',
                    inputName,
                    '-vf',
                    `fps=${Math.max(1, this.exportFps)},${vf}`,
                    '-r',
                    Math.max(1, this.exportFps).toString(),
                    '-an',
                    '-c:v',
                    'mpeg4',
                    '-q:v',
                    qv.toString(),
                    '-pix_fmt',
                    'yuv420p',
                    '-movflags',
                    'faststart',
                    'output.mp4',
                ],
                timeoutMs
            );

            const data = (await this.ffmpeg!.readFile('output.mp4')) as Uint8Array;
            const mp4Bytes = new Uint8Array(data.length);
            mp4Bytes.set(data);
            return mp4Bytes;
        } finally {
            try {
                await this.ffmpeg?.deleteFile?.(inputName);
            } catch {}
            try {
                await this.ffmpeg?.deleteFile?.('output.mp4');
            } catch {}
        }
    }

    private async transcodeRecordedBlobToGif(
        inputBlob: Blob,
        opts?: { sourceW?: number; sourceH?: number; targetW?: number; targetH?: number; durationSec?: number }
    ): Promise<Uint8Array> {
        this.throwIfCancelled();

        await this.initFFmpeg();

        const inputExt = inputBlob.type.includes('mp4') ? 'mp4' : 'webm';
        const inputName = `input.${inputExt}`;

        await this.ffmpeg.writeFile(inputName, await this.blobToUint8Array(inputBlob));

        const needScale =
            opts?.targetW != null &&
            opts?.targetH != null &&
            opts?.sourceW != null &&
            opts?.sourceH != null &&
            (opts.targetW !== opts.sourceW || opts.targetH !== opts.sourceH);

        const scaleFilter = needScale ? `scale=${opts!.targetW}:${opts!.targetH}:flags=bilinear` : null;
        const fpsFilter = `fps=${Math.max(1, this.exportFps)}`;
        const vf = [fpsFilter, scaleFilter].filter(Boolean).join(',');

        try {
            const timeoutMs = Math.max(600000, Math.ceil((opts?.durationSec ?? 0) * 12000));

            await this.execFFmpegWithTimeout(['-fflags', '+genpts', '-i', inputName, '-vf', vf, '-loop', '0', 'output.gif'], timeoutMs);

            const data = (await this.ffmpeg!.readFile('output.gif')) as Uint8Array;
            const gifBytes = new Uint8Array(data.length);
            gifBytes.set(data);
            return gifBytes;
        } finally {
            try {
                await this.ffmpeg?.deleteFile?.(inputName);
            } catch {}
            try {
                await this.ffmpeg?.deleteFile?.('output.gif');
            } catch {}
        }
    }

    private async writeFrameFromRenderer(r: THREE.WebGLRenderer) {
        this.throwIfCancelled();

        if (!this.ffmpeg) throw new Error('ffmpeg not initialized');

        // Reserve index FIRST (sync) to avoid races with concurrent writes
        const index = this.framesWritten++;
        const name = `frame${index.toString().padStart(4, '0')}.${this.frameExt}`;

        const src = r.domElement as HTMLCanvasElement;
        const w = src.width;
        const h = src.height;

        const frameCanvas = document.createElement('canvas');
        frameCanvas.width = w;
        frameCanvas.height = h;
        const frameCtx = frameCanvas.getContext('2d');
        if (!frameCtx) throw new Error('Unable to create frame capture canvas');

        frameCtx.drawImage(src, 0, 0);

        const buf = await this.canvasToArrayBuffer(frameCanvas, 'image/jpeg', this.jpegQuality);

        this.throwIfCancelled();

        await this.ffmpeg.writeFile(name, new Uint8Array(buf));
    }

    // -------------------------------
    // Export resolution logic
    // -------------------------------
    private applyResolutionPreset(preset: ResolutionPreset) {
        const cfg = RESOLUTION_PRESETS[preset];
        this.exportMaxW = cfg.maxW;
        this.exportMaxH = cfg.maxH;
    }

    private alignVideoDimension(value: number, alignment: number) {
        const floored = Math.max(2, Math.floor(value));
        const safeAlignment = Math.max(2, Math.floor(alignment));
        return Math.max(safeAlignment, floored - (floored % safeAlignment));
    }

    /**
     * Compute export sizes in a way that:
     * - keeps aspect ratio correct
     * - respects preset max pixel box (1080p/1440p/4K or match_viewer)
     * - respects DPR cap to avoid retina blow-ups
     * - keeps the visible canvas CSS size stable by using setSize(cssW, cssH, false)
     *
     * Returns:
     *  - exportDpr: the effective pixel ratio used during export
     *  - cssW/cssH: values to pass to renderer.setSize(cssW, cssH, false)
     *  - targetW/targetH: resulting drawing buffer size in pixels (useful for composer.setSize)
     */
    private computeExportSizeFromRenderer(r: THREE.WebGLRenderer, dimensionAlignment = 2) {
        const snap = this.getRendererSnapshot(r);
        const exportDpr = Math.min(window.devicePixelRatio || 1, this.exportDprCap);

        const aspect = snap.w / Math.max(1, snap.h);

        let targetW: number;
        let targetH: number;

        // match_viewer (but DPR-capped)
        if (this.exportMaxW === Number.MAX_SAFE_INTEGER || this.exportMaxH === Number.MAX_SAFE_INTEGER) {
            targetW = Math.floor(snap.w * exportDpr);
            targetH = Math.floor(snap.h * exportDpr);
        } else {
            // Fit aspect inside preset pixel box
            const boxW = this.exportMaxW;
            const boxH = this.exportMaxH;

            if (aspect >= boxW / boxH) {
                targetW = boxW;
                targetH = Math.floor(boxW / aspect);
            } else {
                targetH = boxH;
                targetW = Math.floor(boxH * aspect);
            }
        }

        targetW = this.alignVideoDimension(targetW, dimensionAlignment);
        targetH = this.alignVideoDimension(targetH, dimensionAlignment);

        const cssW = Math.max(2, Math.floor(targetW / exportDpr));
        const cssH = Math.max(2, Math.floor(targetH / exportDpr));

        return { exportDpr, cssW, cssH, targetW, targetH };
    }

    // -------------------------------
    // Export entry point
    // -------------------------------
    async exportVideo(
        variant: VideoExportVariant,
        format: VideoExportFormat,
        quality: number,
        easeIn: boolean,
        settings?: Partial<VideoExportSettings>
    ): Promise<Uint8Array> {
        // Merge: defaults -> UI params -> advanced override
        const s: VideoExportSettings = {
            ...DEFAULT_EXPORT_SETTINGS,
            mp4Quality: quality,
            ...settings,
        };

        this.format = format;
        this.quality = s.mp4Quality;
        this.easeIn = easeIn;

        // Apply advanced params
        this.exportFps = s.fps;
        this.exportDprCap = s.dprCap;
        this.jpegQuality = s.jpegQuality;
        this.applyResolutionPreset(s.resolution);

        // Reset internal state
        this.frameCanvases = [];
        this.framesWritten = 0;
        this.lastTimelapseFrameApplied = null;

        // reset cancel request flag
        this.cancelRequested = false;

        // Init ffmpeg only for MP4
        if (format === 'MP4') {
            await this.initFFmpeg();
        }

        const totalDuration = s.durationSec;

        try {
            // Deterministic capture loops (perfect camera paths)
            switch (variant) {
                case 'lookaround':
                    await this.generate360frames(totalDuration);
                    break;
                case 'orbit':
                    await this.generate360orbit(totalDuration);
                    break;
                case 'timelapse':
                    await this.generateTimelapse(totalDuration);
                    break;
                case 'screenrecording':
                    await this.generateScreenRecording(totalDuration);
                    break;
            }

            this.throwIfCancelled();

            if (format === 'MP4' && this.framesWritten === 0) return new Uint8Array();

            switch (format) {
                case 'GIF':
                    return await this.generateGIF();
                case 'MP4':
                    return await this.generateMP4();
            }
        } catch (e) {
            if (this.cancelRequested) {
                // ensure we leave clean state
                this.updateProgress(undefined);
                return new Uint8Array();
            }
            throw e;
        } finally {
            // If we cancelled, try to clean ffmpeg state
            if (this.cancelRequested) {
                try {
                    this.ffmpeg?.terminate?.();
                } catch {}
                this.ffmpeg = null;
            }
        }

        return new Uint8Array();
    }

    // -------------------------------
    // Screen recording
    // -------------------------------
    async generateScreenRecording(totalDuration: number) {
        const scene: THREE.Scene = getViewer().scene;
        const fps = this.exportFps;
        const frames = Math.max(1, Math.round(totalDuration * fps));

        this.updateProgress(0);
        await this.flushUI();

        const r = renderer();
        if (!r || !scene) return;

        const snap = this.getRendererSnapshot(r);
        const { exportDpr, cssW, cssH, targetW, targetH } = this.computeExportSizeFromRenderer(r);

        try {
            // Apply capped DPR and export size without changing CSS
            (r as any).setPixelRatio?.(exportDpr);
            r.setSize(cssW, cssH, false);

            // If postprocessing is active in this mode, keep composer consistent
            postProcess?.composer?.setSize?.(targetW, targetH);

            for (let i = 0; i < frames; i++) {
                this.throwIfCancelled();

                r.render(scene, camera);

                // MP4 writes frames progressively; GIF stores in memory
                if (this.format === 'MP4') await this.queueFrameWrite(r);
                else this.addToFrameCanvases(r);

                await this.maybeUpdateUI(i, frames);
                await this.maybeYieldToBrowser(i);
            }

            // make sure all queued writes are finished before encoding
            if (this.format === 'MP4') {
                await this.flushFrameWrites();
            }
        } finally {
            this.restoreRenderer(r, snap);
        }
    }

    // -------------------------------
    // Camera Controls
    // -------------------------------
    private snapCameraAndControls(controls: any) {
        return {
            camPos: camera.position.clone(),
            camQuat: camera.quaternion.clone(),
            camUp: camera.up.clone(),
            target: controls?.target ? controls.target.clone() : null,
            enabled: controls?.enabled ?? null,
            zoom: (camera as any).zoom ?? null,

            // OrbitControls internal state to ensure perfect restoration
            target0: controls?.target0?.clone?.() ?? null,
            position0: controls?.position0?.clone?.() ?? null,
            zoom0: controls?.zoom0 ?? null,

            enableDamping: controls?.enableDamping ?? null,
        };
    }

    private restoreCameraAndControls(s: any, controls: any) {
        if (!controls) {
            camera.position.copy(s.camPos);
            camera.quaternion.copy(s.camQuat);
            camera.up.copy(s.camUp);

            if (s.zoom != null) {
                (camera as any).zoom = s.zoom;
                camera.updateProjectionMatrix?.();
            }

            camera.updateMatrixWorld(true);

            return;
        }

        const prevEnabled = controls.enabled;
        const prevDamping = controls.enableDamping;

        controls.enabled = false;

        if (typeof controls.enableDamping === 'boolean') {
            controls.enableDamping = false;
        }

        camera.position.copy(s.camPos);
        camera.quaternion.copy(s.camQuat);
        camera.up.copy(s.camUp);

        if (s.zoom != null) {
            (camera as any).zoom = s.zoom;
            camera.updateProjectionMatrix?.();
        }

        if (controls.target && s.target) {
            controls.target.copy(s.target);
        }

        if (controls.target0 && s.target0) controls.target0.copy(s.target0);
        if (controls.position0 && s.position0) controls.position0.copy(s.position0);
        if (s.zoom0 != null && 'zoom0' in controls) controls.zoom0 = s.zoom0;

        camera.updateMatrixWorld(true);

        controls.update?.();
        camera.updateMatrixWorld(true);

        requestAnimationFrame(() => {
            controls.update?.();
            camera.updateMatrixWorld(true);

            if (typeof prevDamping === 'boolean') {
                controls.enableDamping = prevDamping;
            }

            controls.enabled = s.enabled ?? prevEnabled;
        });
    }

    // -------------------------------
    // Timelapse
    // -------------------------------
    async generateTimelapse(totalDuration: number) {
        const scene: THREE.Scene = getViewer().scene;
        const fps = this.exportFps;
        const frames = Math.max(1, Math.round(totalDuration * fps));

        this.updateProgress(0);
        await this.flushUI();

        const actLayer = grid.getActiveLayer();
        const collectionLen = actLayer.getFrameCollection().length;

        const r = renderer();
        if (!r || !scene) return;

        const snap = this.getRendererSnapshot(r);
        const { exportDpr, cssW, cssH, targetW, targetH } = this.computeExportSizeFromRenderer(r);

        // Cache segments once; we still need it to know current selection, but we avoid per-iteration reads when possible
        const segments = getSegments();

        try {
            (r as any).setPixelRatio?.(exportDpr);
            r.setSize(cssW, cssH, false);
            postProcess?.composer?.setSize?.(targetW, targetH);

            for (let i = 0; i < frames; i++) {
                this.throwIfCancelled();

                const t = frames <= 1 ? 0 : i / (frames - 1);
                const frameSel = Math.floor(t * Math.max(0, collectionLen - 1));

                if (this.lastTimelapseFrameApplied !== frameSel) {
                    this.lastTimelapseFrameApplied = frameSel;

                    if (frameSel !== segments.frameSelected) {
                        actLayer.setFrame(frameSel);

                        // Heavy path: only run when we truly changed frame
                        if (actLayer.status.operator.SHOW_LINES) {
                            actLayer.raysHandler?.createRays([0], 'visible');
                        }

                        const frame = actLayer.getActiveFrame();
                        if (frame) {
                            (frame as any).normalizeSensor?.(getElement().normalized);
                            frame.updateShadows();
                        }

                        switcher.REFRESH_GRADIENT = true;
                    }
                }

                // Render + capture
                r.render(scene, camera);

                if (this.format === 'MP4') await this.queueFrameWrite(r);
                else this.addToFrameCanvases(r);

                await this.maybeUpdateUI(i, frames);
                await this.maybeYieldToBrowser(i);
            }

            // make sure all queued writes are finished before encoding
            if (this.format === 'MP4') {
                await this.flushFrameWrites();
            }
        } finally {
            this.restoreRenderer(r, snap);
        }
    }

    // -------------------------------
    // Lookaround
    // -------------------------------
    async generate360frames(totalDuration: number) {
        const viewer = getViewer();
        const scene: THREE.Scene = viewer.scene!;
        const controlsData = viewer.cameraHelper.getOrbitControls();
        const camSnap = this.snapCameraAndControls(controlsData);

        const { operator } = grid.getActiveLayer().status;
        const sensorPoint = Grid.sensorPoints[operator.ACTIVE_SP_INDEX];
        const position = sensorPoint.getCenter();

        const fps = this.exportFps;
        const frames = Math.max(1, Math.round(totalDuration * fps));
        const axis = new THREE.Vector3(0, 0, 1);

        controlsData.reset();

        camera.position.set(position.x, position.z, -position.y);
        camera.updateMatrixWorld(true);

        this.updateProgress(0);
        await this.flushUI();

        const r = renderer();
        if (!r || !scene) return;

        const snap = this.getRendererSnapshot(r);
        const { exportDpr, cssW, cssH, targetW, targetH } = this.computeExportSizeFromRenderer(r);

        try {
            (r as any).setPixelRatio?.(exportDpr);
            r.setSize(cssW, cssH, false);
            postProcess?.composer?.setSize?.(targetW, targetH);

            for (let i = 0; i < frames; i++) {
                this.throwIfCancelled();

                const t = frames <= 1 ? 0 : i / (frames - 1);
                const easeInOut = t * t * (3 - 2 * t); // smoothstep
                const angle = this.easeIn ? easeInOut * 2 * Math.PI : t * 2 * Math.PI;

                // Move look direction around the sensor point
                const dir = new THREE.Vector3(0, 1, 0).applyAxisAngle(axis, angle).normalize();
                controlsData.target.set(position.x + dir.x, position.z + dir.z, -position.y - dir.y);

                // force OrbitControls to update camera NOW
                controlsData.update?.();
                camera.updateMatrixWorld(true);

                r.render(scene, camera);

                if (this.format === 'MP4') await this.queueFrameWrite(r);
                else this.addToFrameCanvases(r);

                await this.maybeUpdateUI(i, frames);
                await this.maybeYieldToBrowser(i);
            }

            if (this.format === 'MP4') {
                await this.flushFrameWrites();
            }
        } finally {
            this.restoreRenderer(r, snap);

            this.restoreCameraAndControls(camSnap, controlsData);

            r.render(scene, camera);
        }

        if (this.format !== 'MP4' && this.frameCanvases.length > 0) {
            this.frameCanvases.shift();
        }
    }

    // -------------------------------
    // Orbit
    // -------------------------------
    async generate360orbit(totalDuration: number) {
        const viewer = getViewer();
        const scene: THREE.Scene = viewer.scene!;
        const controlsData = viewer.cameraHelper.getOrbitControls();
        const camSnap = this.snapCameraAndControls(controlsData);

        const position = Grid.siteVolumeCenter;

        // IMPORTANT: keep these computed once to avoid orbit drift
        const elevation = camera.position.y;
        const distance = camera.position.distanceTo(position);

        controlsData.reset();

        controlsData.target.set(position.x, position.z, -position.y);
        camera.updateMatrixWorld(true);

        const fps = this.exportFps;
        const frames = Math.max(1, Math.round(totalDuration * fps));

        this.updateProgress(0);
        await this.flushUI();

        const r = renderer();
        if (!r || !scene) return;

        const snap = this.getRendererSnapshot(r);
        const { exportDpr, cssW, cssH, targetW, targetH } = this.computeExportSizeFromRenderer(r);

        try {
            (r as any).setPixelRatio?.(exportDpr);
            r.setSize(cssW, cssH, false);

            postProcess?.composer?.setSize?.(targetW, targetH);

            for (let i = 0; i < frames; i++) {
                this.throwIfCancelled();

                const t = frames <= 1 ? 0 : i / (frames - 1);
                const easeInOut = t * t * (3 - 2 * t);
                const angle = this.easeIn ? easeInOut * 2 * Math.PI : t * 2 * Math.PI;

                camera.position.set(
                    controlsData.target.x + distance * Math.cos(angle),
                    Math.abs(elevation),
                    controlsData.target.z + distance * Math.sin(angle)
                );
                camera.lookAt(controlsData.target);
                camera.updateMatrixWorld(true);

                postProcess.applyPostProcess(0);
                postProcess.composer.render();

                if (this.format === 'MP4') await this.queueFrameWrite(r);
                else this.addToFrameCanvases(r);

                await this.maybeUpdateUI(i, frames);
                await this.maybeYieldToBrowser(i);
            }

            if (this.format === 'MP4') {
                await this.flushFrameWrites();
            }
        } finally {
            this.restoreRenderer(r, snap);

            this.restoreCameraAndControls(camSnap, controlsData);

            postProcess?.applyPostProcess?.(0);
            postProcess?.composer?.render?.();
        }

        if (this.format !== 'MP4' && this.frameCanvases.length > 0) {
            this.frameCanvases.shift();
        }
    }

    // -------------------------------
    // Begin Free Screen Recording
    // -------------------------------
    async beginFreeScreenRecording(
        format: VideoExportFormat,
        quality: number,
        easeIn: boolean,
        settings?: Partial<VideoExportSettings>,
        onSourceStreamEnded?: () => void
    ): Promise<number> {
        const s: VideoExportSettings = {
            ...DEFAULT_EXPORT_SETTINGS,
            mp4Quality: quality,
            ...settings,
        };

        this.format = format;
        this.quality = s.mp4Quality;
        this.easeIn = easeIn;

        this.exportFps = s.fps;
        this.exportDprCap = s.dprCap;
        this.jpegQuality = s.jpegQuality;
        this.applyResolutionPreset(s.resolution);

        // reset stato
        this.cancelRequested = false;
        this.frameCanvases = [];
        this.framesWritten = 0;

        const r = renderer();
        const scene: THREE.Scene = getViewer().scene;
        if (!r || !scene) {
            throw new Error('Unable to start free screen recording: renderer is not ready');
        }

        // snapshot renderer
        const snap = this.getRendererSnapshot(r);

        // Native browser H.264 encoders can corrupt captured canvas streams whose
        // frame height is not macroblock-aligned.
        const { exportDpr, cssW, cssH, targetW, targetH } = this.computeExportSizeFromRenderer(r, 16);

        const fps = Math.max(1, this.exportFps);
        const frameIntervalMs = 1000 / fps;

        const sourceCanvas = r.domElement as HTMLCanvasElement;
        const recordingMimeType = this.getSupportedRecordingMimeType();
        const canUseMediaRecorder = (format === 'MP4' || format === 'GIF') && typeof MediaRecorder !== 'undefined' && !!recordingMimeType;

        const loop = (ts: number) => {
            const rec = this.freeRec;
            if (!rec || !rec.running) return;

            const dt = ts - rec.lastFrameTs;
            if (dt >= rec.frameIntervalMs) {
                rec.lastFrameTs = ts;

                if (rec.mode === 'mediarecorder') {
                    this.renderCurrentViewerFrame(r, scene);
                    rec.canvasCaptureTrack?.requestFrame?.();
                } else if (rec.format === 'MP4') {
                    this.renderCurrentViewerFrame(r, scene);
                    this.queueFreeRecordingFrame(r);
                } else if (rec.format === 'GIF') {
                    this.renderCurrentViewerFrame(r, scene);
                    this.addToFrameCanvases(r);
                }
            }

            rec.rafId = requestAnimationFrame(loop);
        };

        if (canUseMediaRecorder) {
            const mediaDevices = navigator.mediaDevices as MediaDevices | undefined;
            const prefersDisplayCapture = typeof mediaDevices?.getDisplayMedia === 'function';

            // Display capture is used only for the browser-native recording indicator and
            // stop-sharing lifecycle. The exported pixels come from the clean WebGL canvas.
            const sourceStream = prefersDisplayCapture
                ? await mediaDevices!.getDisplayMedia({
                      video: {
                          frameRate: fps,
                          width: { ideal: targetW },
                          height: { ideal: targetH },
                          ...({ resizeMode: 'crop-and-scale' } as any),
                          displaySurface: 'browser',
                      } as MediaTrackConstraints,
                      audio: false,
                      monitorTypeSurfaces: 'exclude',
                      preferCurrentTab: true,
                      selfBrowserSurface: 'include',
                      surfaceSwitching: 'exclude',
                      systemAudio: 'exclude',
                  } as DisplayMediaStreamOptions)
                : null;
            const sourceTrack = sourceStream?.getVideoTracks()[0] ?? null;

            await this.applyPreferredCaptureConstraints(sourceTrack, targetW, targetH, fps);

            if (sourceTrack && onSourceStreamEnded) {
                sourceTrack.addEventListener(
                    'ended',
                    () => {
                        onSourceStreamEnded();
                    },
                    { once: true }
                );
            }

            (r as any).setPixelRatio?.(exportDpr);
            r.setSize(cssW, cssH, false);
            postProcess?.composer?.setSize?.(targetW, targetH);
            this.renderCurrentViewerFrame(r, scene);

            const mediaStream = sourceCanvas.captureStream(fps);
            const canvasCaptureTrack = (mediaStream.getVideoTracks()[0] ?? null) as
                | (MediaStreamTrack & { requestFrame?: () => void })
                | null;
            const recordedChunks: Blob[] = [];

            let stopResolve: (() => void) | null = null;
            let stopReject: ((reason?: any) => void) | null = null;
            const stopPromise = new Promise<void>((resolve, reject) => {
                stopResolve = resolve;
                stopReject = reject;
            });

            const mediaRecorder = new MediaRecorder(mediaStream, {
                mimeType: recordingMimeType,
                videoBitsPerSecond: this.getRecordingVideoBitrate(sourceCanvas.width, sourceCanvas.height, fps),
            });

            mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    recordedChunks.push(event.data);
                }
            };
            mediaRecorder.onerror = (event: Event) => {
                const err = (event as ErrorEvent).error ?? new Error('MediaRecorder failed');
                stopReject?.(err);
            };
            mediaRecorder.onstop = () => stopResolve?.();
            mediaRecorder.start(1000);
            const startedAtMs = performance.now();

            this.freeRec = {
                format,
                fps,
                mode: 'mediarecorder',
                snap,
                targetW,
                targetH,
                captureW: sourceCanvas.width,
                captureH: sourceCanvas.height,
                mediaRecorder,
                mediaStream,
                sourceStream,
                canvasCaptureTrack,
                recordedChunks,
                recordedMimeType: mediaRecorder.mimeType || recordingMimeType,
                stopPromise,
                running: true,
                rafId: null,
                lastFrameTs: startedAtMs,
                frameIntervalMs,
                startedAtMs,
                stoppedAtMs: null,
                pendingWrites: [],
                maxPendingWrites: 0,
            };

            this.freeRec.rafId = requestAnimationFrame(loop);
            return startedAtMs;
        }

        if (format === 'MP4') {
            await this.initFFmpeg();
        }

        (r as any).setPixelRatio?.(exportDpr);
        r.setSize(cssW, cssH, false);
        postProcess?.composer?.setSize?.(targetW, targetH);

        const captureW = sourceCanvas.width;
        const captureH = sourceCanvas.height;
        const startedAtMs = performance.now();

        this.freeRec = {
            format,
            fps,
            mode: 'framecapture',
            snap,
            targetW,
            targetH,
            captureW,
            captureH,
            mediaRecorder: null,
            mediaStream: null,
            sourceStream: null,
            canvasCaptureTrack: null,
            recordedChunks: [],
            recordedMimeType: null,
            stopPromise: null,

            running: true,
            rafId: null,
            lastFrameTs: startedAtMs,
            frameIntervalMs,

            startedAtMs,
            stoppedAtMs: null,

            pendingWrites: [],
            maxPendingWrites: 1,
        };

        if (format === 'MP4') {
            this.renderCurrentViewerFrame(r, scene);
        }

        this.freeRec.rafId = requestAnimationFrame(loop);
        return startedAtMs;
    }

    // -------------------------------
    // Free Screen Recording Stop
    // -------------------------------
    async finishFreeScreenRecording(): Promise<FreeScreenRecordingResult> {
        const r = renderer();
        if (!this.freeRec || !r) return { data: new Uint8Array(), extension: 'mp4' };

        // stop loop
        this.freeRec.running = false;
        if (this.freeRec.rafId != null) cancelAnimationFrame(this.freeRec.rafId);
        this.freeRec.stoppedAtMs = performance.now();

        const {
            snap,
            format,
            mode,
            captureW,
            captureH,
            targetW,
            targetH,
            pendingWrites,
            startedAtMs,
            stoppedAtMs,
            mediaRecorder,
            mediaStream,
            sourceStream,
            recordedChunks,
            recordedMimeType,
            stopPromise,
        } = this.freeRec;
        this.freeRec = null;

        // restore renderer immediately
        this.restoreRenderer(r, snap);

        try {
            if (mode === 'mediarecorder') {
                if (mediaRecorder && mediaRecorder.state !== 'inactive') {
                    try {
                        mediaRecorder.requestData?.();
                    } catch {}
                    mediaRecorder.stop();
                }

                let recorderStopped = true;
                try {
                    recorderStopped = await this.waitForRecorderStop(stopPromise);
                } finally {
                    mediaStream?.getTracks().forEach((track) => track.stop());
                    sourceStream?.getTracks().forEach((track) => track.stop());
                }
                if (!recorderStopped) {
                    console.warn('MediaRecorder stop timed out. Finalizing with collected chunks.');
                }

                const recordedBlob = new Blob(recordedChunks, {
                    type: recordedMimeType || 'video/webm',
                });

                if (recordedBlob.size === 0) {
                    throw new Error('Free screen recording produced no video data');
                }

                this.updateProgress(35);
                await CommitProgressUI();

                if (format === 'MP4') {
                    if (recordedMimeType?.includes('mp4')) {
                        return { data: await this.blobToUint8Array(recordedBlob), extension: 'mp4' };
                    }

                    this.updateProgress(60);
                    await CommitProgressUI();

                    const recordingDurationSec = Math.max(
                        1 / Math.max(1, this.exportFps),
                        ((stoppedAtMs ?? startedAtMs) - startedAtMs) / 1000
                    );

                    return {
                        data: await this.transcodeRecordedBlobToMp4(recordedBlob, {
                            sourceW: captureW,
                            sourceH: captureH,
                            targetW,
                            targetH,
                            durationSec: recordingDurationSec,
                        }),
                        extension: 'mp4',
                    };
                }

                this.updateProgress(60);
                await CommitProgressUI();

                const recordingDurationSec = Math.max(1 / Math.max(1, this.exportFps), ((stoppedAtMs ?? startedAtMs) - startedAtMs) / 1000);

                return {
                    data: await this.transcodeRecordedBlobToGif(recordedBlob, {
                        sourceW: captureW,
                        sourceH: captureH,
                        targetW,
                        targetH,
                        durationSec: recordingDurationSec,
                    }),
                    extension: 'gif',
                };
            }

            if (format === 'MP4') {
                const recordingDurationSec = Math.max(1 / Math.max(1, this.exportFps), ((stoppedAtMs ?? startedAtMs) - startedAtMs) / 1000);
                const encodedFps = this.framesWritten > 0 ? this.framesWritten / recordingDurationSec : this.exportFps;
                this.exportFps = Math.max(1, Number(encodedFps.toFixed(3)));

                this.updateProgress(10);
                await CommitProgressUI();

                if (pendingWrites.length > 0) {
                    await Promise.all(pendingWrites);
                }

                this.updateProgress(40);
                await CommitProgressUI();

                if (this.cancelRequested) throw this.cancelledError;

                if (this.framesWritten === 0) {
                    throw new Error('Free screen recording captured no frames');
                }

                return {
                    data: await this.generateMP4({
                        sourceW: captureW,
                        sourceH: captureH,
                        targetW,
                        targetH,
                    }),
                    extension: 'mp4',
                };
            }

            if (this.frameCanvases.length === 0) {
                throw new Error('Free screen recording captured no frames');
            }

            this.updateProgress(40);
            await CommitProgressUI();

            return {
                data: await this.generateGIF(),
                extension: 'gif',
            };
        } catch (e: any) {
            if (this.cancelRequested || e === this.cancelledError || e?.message?.includes('FFmpeg.terminate')) {
                return { data: new Uint8Array(), extension: 'mp4' };
            }

            throw e;
        } finally {
            try {
                this.ffmpeg?.terminate?.();
            } catch {}

            this.ffmpeg = null;
            this.updateProgress(undefined);
        }
    }

    // -------------------------------
    // GIF generation
    // -------------------------------
    async generateGIF(): Promise<Uint8Array> {
        return new Promise(async (resolve) => {
            console.log('writing ' + this.frameCanvases.length + ' frames to GIF');
            const gifQuality = Math.round(20 - (this.quality / 100) * 19);

            const ditherAlgs = [
                'Atkinson',
                'Atkinson-serpentine',
                'FalseFloydSteinberg',
                'FalseFloidSteinberg-serpentine',
                'FloydSteinberg',
                'FloydSteinberg-serpentine',
                'Stucki',
                'Stucki-serpentine',
            ];
            const GIF = await this.loadGIFJS();
            const gif = new GIF({
                workerScript: gifWorkerBlobURL,
                workers: 4,
                quality: gifQuality,
                dither: ditherAlgs[0],
            });

            this.updateProgress(50);
            await CommitProgressUI();

            const interval = Math.max(1, Math.round(1000 / Math.max(1, this.exportFps)));
            for (let i = 0; i < this.frameCanvases.length; i++) {
                gif.addFrame(this.frameCanvases[i], { copy: true, delay: interval });
                this.updateProgress(Math.round(50 + ((i + 1) / this.frameCanvases.length) * 40));
                await CommitProgressUI();
            }

            gif.on('finished', async (blob: Blob) => {
                const arrayBuffer = await blob.arrayBuffer();
                const gifBytes = new Uint8Array(arrayBuffer);
                this.frameCanvases = [];
                resolve(gifBytes);
            });

            gif.render();
        });
    }

    async loadGIFJS(): Promise<any> {
        if ((window as any).GIF) return (window as any).GIF;

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/visualizer/gif.js';
            script.onload = () => resolve((window as any).GIF);
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    // -------------------------------
    // MP4 encoding
    // -------------------------------
    async generateMP4(opts?: { sourceW?: number; sourceH?: number; targetW?: number; targetH?: number }): Promise<Uint8Array> {
        this.throwIfCancelled();

        try {
            if (!this.ffmpeg) {
                await this.initFFmpeg();
            }

            await this.flushFrameWrites();

            console.log('encoding ' + this.framesWritten + ' frames to MP4');

            this.updateProgress(90);
            await CommitProgressUI();

            let qv = Math.round((100 - this.quality) / 3);
            qv = Math.min(Math.max(qv, 1), 31);

            const fps = this.exportFps;

            const needScale =
                opts?.targetW != null &&
                opts?.targetH != null &&
                opts?.sourceW != null &&
                opts?.sourceH != null &&
                (opts.targetW !== opts.sourceW || opts.targetH !== opts.sourceH);

            const vf = needScale
                ? `scale=${opts!.targetW}:${opts!.targetH}:flags=bilinear,pad=ceil(iw/2)*2:ceil(ih/2)*2`
                : 'pad=ceil(iw/2)*2:ceil(ih/2)*2';

            await this.ffmpeg.exec([
                '-framerate',
                fps.toString(),
                '-i',
                `frame%04d.${this.frameExt}`,
                '-vf',
                vf,
                '-c:v',
                'mpeg4',
                '-q:v',
                qv.toString(),
                '-pix_fmt',
                'yuv420p',
                '-movflags',
                'faststart',
                'output.mp4',
            ]);

            this.updateProgress(100);
            await CommitProgressUI();

            this.throwIfCancelled();

            const data = (await this.ffmpeg.readFile('output.mp4')) as Uint8Array;
            const mp4Bytes = new Uint8Array(data.length);
            mp4Bytes.set(data);

            // Cleanup
            for (let i = 0; i < this.framesWritten; i++) {
                const name = `frame${i.toString().padStart(4, '0')}.${this.frameExt}`;
                await this.ffmpeg.deleteFile(name);
            }
            await this.ffmpeg.deleteFile('output.mp4');

            this.ffmpeg.terminate();
            this.ffmpeg = null;

            return mp4Bytes;
        } catch (e: any) {
            if (this.cancelRequested || e === this.cancelledError || e?.message?.includes('FFmpeg.terminate')) {
                return new Uint8Array();
            }
            throw e;
        }
    }

    // -------------------------------
    // Cancel Encoding
    // -------------------------------
    public cancelEncoding() {
        this.cancelRequested = true;

        // Hard-stop ffmpeg if it’s currently running.
        // This is the important part for “user not blocked”.
        try {
            this.ffmpeg?.terminate?.();
        } catch (e) {
            // ignore
        } finally {
            this.ffmpeg = null;
        }
    }

    private throwIfCancelled() {
        if (!this.cancelRequested) return;
        throw this.cancelledError;
    }

    // -------------------------------
    // GIF-only frame capture
    // -------------------------------
    private addToFrameCanvases(r: THREE.WebGLRenderer) {
        const canvas = document.createElement('canvas');
        canvas.width = r.domElement.width;
        canvas.height = r.domElement.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(r.domElement, 0, 0);
        this.frameCanvases.push(canvas);
    }
}
